import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { devLog, devError, devWarn } from '../utils/logger';

export default function ModelViewer({ modelUrl, className = "" }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!modelUrl || !containerRef.current) return;

    let scene, camera, renderer, controls, animationFrameId;

    // Initialize Three.js
    const init = () => {
      const container = containerRef.current;
      const width = container.clientWidth;
      const height = container.clientHeight || 400;

      // Scene
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1a1a2e);

      // Camera
      camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      camera.position.set(2, 2, 5);

      // Renderer
      renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      container.appendChild(renderer.domElement);

      // Lights
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(5, 10, 7.5);
      directionalLight.castShadow = true;
      scene.add(directionalLight);

      const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
      directionalLight2.position.set(-5, 5, -5);
      scene.add(directionalLight2);

      // Grid helper for reference
      const gridHelper = new THREE.GridHelper(10, 10, 0x444444, 0x222222);
      scene.add(gridHelper);

      // Controls
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.screenSpacePanning = false;
      controls.minDistance = 1;
      controls.maxDistance = 50;

      // Load model
      const loader = new GLTFLoader();
      loader.load(
        modelUrl,
        (gltf) => {
          const model = gltf.scene;
          
          // Center and scale model
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          model.scale.multiplyScalar(scale);
          
          model.position.sub(center.multiplyScalar(scale));
          
          scene.add(model);
          setLoading(false);
        },
        (progress) => {
          // Progress callback
          devLog('Loading:', (progress.loaded / progress.total * 100) + '%');
        },
        (error) => {
          devError('Error loading model:', error);
          setError(true);
          setLoading(false);
        }
      );

      // Animation loop
      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // Handle window resize
      const handleResize = () => {
        const width = container.clientWidth;
        const height = container.clientHeight || 400;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      };
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        if (renderer) {
          renderer.dispose();
          container.removeChild(renderer.domElement);
        }
        if (controls) {
          controls.dispose();
        }
      };
    };

    const cleanup = init();

    return () => {
      if (cleanup) cleanup();
    };
  }, [modelUrl]);

  if (!modelUrl) {
    return (
      <div className={`flex items-center justify-center bg-secondary/30 rounded-lg ${className}`}>
        <p className="text-muted-foreground text-sm">No 3D model available</p>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}>
      <div 
        ref={containerRef} 
        className="w-full h-full min-h-[400px]"
        style={{ minHeight: '400px' }}
      />
      
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/90">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-muted-foreground">Loading 3D model...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/90">
          <div className="text-center">
            <p className="text-sm text-destructive">Failed to load model</p>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded-lg backdrop-blur-sm">
          <p className="font-medium mb-1">Controls:</p>
          <p>🖱️ Drag to rotate</p>
          <p>🔍 Scroll to zoom</p>
        </div>
      )}
    </div>
  );
}
